import { Wrench } from 'lucide-react';
import React from 'react';
import type { MaintenanceWindow } from 'src/services/maintenanceService';
import BrandLogo from 'src/shared/components/BrandLogo';
import { ContainerBox, ContentCard, FlexContainer, StackContainer, TypographyText } from 'src/tef/components';

interface MaintenanceScreenProps {
  window: MaintenanceWindow | null;
}

const formatEasternTime = (iso: string | undefined) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/New_York',
      timeZoneName: 'short',
    });
  } catch {
    return '';
  }
};

const MaintenanceScreen: React.FC<MaintenanceScreenProps> = ({ window }) => {
  const message =
    window?.maintenance_message?.trim() ||
    'Tropel is currently undergoing scheduled maintenance. We will be back shortly.';
  const expectedReturn = formatEasternTime(window?.scheduled_end);

  return (
    <ContainerBox className="min-h-screen flex items-center justify-center p-4 sm:p-6 bg-slate-50 dark:bg-slate-950">
      <ContentCard variant="elevated" className="w-full max-w-xl">
        <ContainerBox className="px-6 py-10 sm:px-12 sm:py-12">
          <StackContainer spacing="lg" align="center">
            <FlexContainer
              align="center"
              gap="xs"
              className="px-3 py-1.5 rounded-full border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/20"
            >
              <Wrench className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <TypographyText
                variant={{ size: 'xs' }}
                className="font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 whitespace-nowrap"
              >
                Scheduled Maintenance
              </TypographyText>
            </FlexContainer>
            <BrandLogo variant="auto" size="md" withClearSpace={false} />
            <ContainerBox className="w-12 h-px bg-slate-200 dark:bg-slate-800" />
            <StackContainer spacing="sm" align="center">
              <TypographyText
                as="h1"
                className="font-semibold tracking-tight text-slate-900 dark:text-slate-100 text-center text-xl sm:text-2xl"
              >
                Service Temporarily Unavailable
              </TypographyText>
              <TypographyText color="secondary" className="text-center max-w-md leading-relaxed whitespace-pre-line">
                {message}
              </TypographyText>
            </StackContainer>
            {expectedReturn && (
              <ContainerBox className="w-full rounded-lg border border-slate-200 dark:border-slate-800 divide-y divide-slate-200 dark:divide-slate-800">
                <ContainerBox className="px-4 py-3 sm:px-5 sm:py-3.5 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <TypographyText
                    variant={{ size: 'xs' }}
                    color="secondary"
                    className="uppercase tracking-wider font-medium whitespace-nowrap"
                  >
                    Expected Return
                  </TypographyText>
                  <TypographyText
                    variant={{ size: 'sm' }}
                    className="font-semibold text-slate-900 dark:text-slate-100 sm:text-right"
                  >
                    {expectedReturn}
                  </TypographyText>
                </ContainerBox>
                <ContainerBox className="px-4 py-3 sm:px-5 sm:py-3.5 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <TypographyText
                    variant={{ size: 'xs' }}
                    color="secondary"
                    className="uppercase tracking-wider font-medium whitespace-nowrap"
                  >
                    Status
                  </TypographyText>
                  <FlexContainer align="center" gap="xs">
                    <ContainerBox className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
                    <TypographyText
                      variant={{ size: 'sm' }}
                      className="font-semibold text-slate-900 dark:text-slate-100"
                    >
                      In Progress
                    </TypographyText>
                  </FlexContainer>
                </ContainerBox>
              </ContainerBox>
            )}
            <TypographyText variant={{ size: 'xs' }} color="secondary" className="text-center">
              This page will refresh automatically when service is restored.
            </TypographyText>
          </StackContainer>
        </ContainerBox>
      </ContentCard>
    </ContainerBox>
  );
};

export default MaintenanceScreen;
